import React, { useMemo, useState } from 'react'
import { TextareaProps } from './types'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'

/**
 * Componente Textarea minimalista con diseño limpio y sutil
 */
const Textarea: React.FC<TextareaProps> = ({
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  mode,
  rows = 4,
  maxLength,
  className = '',
  size = 'md',
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false)

  // Determinar si está en modo readonly según mode
  const isReadOnly = useMemo(() => {
    if (readOnly) return true
    if (mode === 'view') return true
    return false
  }, [readOnly, mode])

  // Clases base según tamaño - minimalista
  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'sm':
        return 'px-2.5 py-1.5 text-sm'
      case 'lg':
        return 'px-3.5 py-2.5 text-base'
      default:
        return 'px-3 py-2 text-sm'
    }
  }, [size])

  // Clases para el contenedor
  const containerClasses = useMemo(() => {
    return `relative ${className}`
  }, [className])

  // Clases para el textarea - diseño minimalista
  const textareaClasses = useMemo(() => {
    // Base minimalista: sin sombras, bordes sutiles
    const baseClasses = `w-full rounded-md border transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-offset-0 resize-none ${sizeClasses} bg-white`
    
    const stateClasses = disabled
      ? 'bg-gray-50 text-gray-500 cursor-not-allowed border-gray-200'
      : isReadOnly
      ? 'bg-gray-50 text-gray-700 border-gray-200 cursor-default'
      : error
      ? 'border-red-300 text-gray-900 focus:ring-red-400 focus:border-red-400'
      : `border-gray-300 text-gray-900 focus:ring-blue-400 focus:border-blue-400 ${isFocused ? '' : 'hover:border-gray-400'}`
    
    return `${baseClasses} ${stateClasses}`
  }, [sizeClasses, disabled, isReadOnly, error, isFocused])

  // Handlers
  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true)
    if (rest.onFocus) rest.onFocus(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false)
    if (rest.onBlur) rest.onBlur(e)
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (rest.onChange) rest.onChange(e)
  }

  // Obtener valor actual para contador
  const currentValue = typeof rest.value === 'string' ? rest.value : ''
  const charCount = currentValue.length

  return (
    <div className={containerClasses}>
      {/* Textarea minimalista */}
      <textarea
        rows={rows}
        disabled={disabled}
        readOnly={isReadOnly}
        maxLength={maxLength}
        className={textareaClasses}
        aria-invalid={error}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        {...rest}
      />

      {/* Contador de caracteres - minimalista */}
      {maxLength && (
        <div className={`mt-1 text-xs text-right ${
          charCount >= maxLength 
            ? 'text-red-500' 
            : 'text-gray-400'
        }`}>
          {charCount} / {maxLength}
        </div>
      )}

      {/* Mensaje de error - minimalista */}
      {error && errorMessage && (
        <div className="mt-1 flex items-center gap-1">
          <ExclamationCircleIcon className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-600">{errorMessage}</span>
        </div>
      )}
    </div>
  )
}

export default React.memo(Textarea)
